import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  where,
} from "firebase/firestore";
import { useParams, useSearchParams } from "react-router-dom";
import { db } from "../../firebase";
import { generateFundReceipt } from "../../utils/generateFundReceipt";

/* -------------------------------------------------------------------------- */
/* Utility functions                                                          */
/* -------------------------------------------------------------------------- */

const compareStrings = (firstValue, secondValue) =>
  String(firstValue || "").localeCompare(String(secondValue || ""), undefined, {
    sensitivity: "base",
  });

const formatAmount = (value) => {
  const amount = Number(value);

  if (!Number.isFinite(amount)) {
    return "0.00";
  }

  return amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDate = (value) => {
  if (!value) {
    return "Not available";
  }

  try {
    const date =
      typeof value?.toDate === "function"
        ? value.toDate()
        : value instanceof Date
          ? value
          : new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "Not available";
    }

    return date.toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return "Not available";
  }
};

const getTimestampValue = (value) => {
  try {
    if (typeof value?.toMillis === "function") {
      return value.toMillis();
    }

    if (typeof value?.toDate === "function") {
      return value.toDate().getTime();
    }

    if (value instanceof Date) {
      return value.getTime();
    }

    if (typeof value === "string") {
      const date = new Date(value);

      return Number.isNaN(date.getTime()) ? 0 : date.getTime();
    }
  } catch {
    return 0;
  }

  return 0;
};

const extractFloor = (flatLabel) => {
  const normalizedLabel = String(flatLabel || "")
    .trim()
    .toUpperCase();

  if (!normalizedLabel) {
    return null;
  }

  if (normalizedLabel.includes("-G") || normalizedLabel.startsWith("G")) {
    return 0;
  }

  const numbers = normalizedLabel.match(/\d+/g);

  if (!numbers?.length) {
    return null;
  }

  const firstNumber = Number(numbers[0]);

  if (!Number.isFinite(firstNumber)) {
    return null;
  }

  return firstNumber >= 100 ? Math.floor(firstNumber / 100) : firstNumber;
};

const normalizeBlockName = (block, index) =>
  String(block?.name || block?.blockName || `Block-${index + 1}`).trim();

const getBlockKey = (block, index) =>
  String(block?.id || block?.name || block?.blockName || `block-${index}`);

const buildFloorOptions = (block) => {
  if (!block) {
    return [];
  }

  const floors = new Set();

  if (block.hasGround) {
    floors.add(0);
  }

  const configuredFloors = Number(block.floors || 0);

  for (let floorNumber = 1; floorNumber <= configuredFloors; floorNumber += 1) {
    floors.add(floorNumber);
  }

  if (Array.isArray(block.flats)) {
    block.flats.forEach((flatLabel) => {
      const floorNumber = extractFloor(flatLabel);

      if (floorNumber !== null) {
        floors.add(floorNumber);
      }
    });
  }

  return Array.from(floors)
    .sort((first, second) => first - second)
    .map((floorNumber) => ({
      value: String(floorNumber),
      label:
        floorNumber === 0
          ? `Ground (${block.groundLabel || "G"})`
          : `Floor ${floorNumber}`,
    }));
};

const deriveFromStartNumber = (startNumberInput) => {
  const value = String(startNumberInput || "").replace(/\D/g, "");

  if (!value) {
    return {
      floorBase: 1,
      indexStart: 1,
      indexWidth: 2,
    };
  }

  if (value.length <= 2) {
    return {
      floorBase: 1,
      indexStart: parseInt(value, 10),
      indexWidth: Math.max(2, value.length),
    };
  }

  return {
    floorBase: parseInt(value.slice(0, -2), 10),
    indexStart: parseInt(value.slice(-2), 10),
    indexWidth: 2,
  };
};

const buildFlatOptions = (block, blockName, selectedFloorNumber) => {
  if (!block || selectedFloorNumber === "") {
    return [];
  }

  const selectedFloor = Number(selectedFloorNumber);

  if (!Number.isFinite(selectedFloor)) {
    return [];
  }

  if (Array.isArray(block.flats) && block.flats.length > 0) {
    return block.flats
      .map((flat) => String(flat).trim())
      .filter(Boolean)
      .filter((flat) => extractFloor(flat) === selectedFloor)
      .sort(compareStrings);
  }

  const totalFlats = Number(block.flatsPerFloor || 0);

  if (!Number.isFinite(totalFlats) || totalFlats <= 0) {
    return [];
  }

  const { floorBase, indexStart, indexWidth } = deriveFromStartNumber(
    block.startNumberInput || "101",
  );

  const flatLabels = [];

  if (selectedFloor === 0) {
    for (let index = 0; index < totalFlats; index += 1) {
      const flatIndex = indexStart + index;

      flatLabels.push(
        `${blockName}-${block.groundLabel || "G"}${String(flatIndex).padStart(
          indexWidth,
          "0",
        )}`,
      );
    }

    return flatLabels;
  }

  const calculatedFloorCode = floorBase + selectedFloor - 1;

  for (let index = 0; index < totalFlats; index += 1) {
    const flatIndex = indexStart + index;

    flatLabels.push(
      `${blockName}-${calculatedFloorCode}${String(flatIndex).padStart(
        indexWidth,
        "0",
      )}`,
    );
  }

  return flatLabels;
};

const getReadableError = (error) => {
  if (error?.code === "permission-denied") {
    return (
      error?.message || "Receipt access was denied. Check the Firestore rules."
    );
  }

  if (error?.code === "failed-precondition") {
    return error?.message || "Firestore requires an index.";
  }

  if (error?.code === "unavailable") {
    return (
      error?.message ||
      "The service is unavailable. Check your internet connection."
    );
  }

  return error?.message || "Something went wrong. Please try again.";
};

const getReceiptTypeLabel = (receipt) => {
  const sourceType = String(receipt?.sourceType || "")
    .trim()
    .toLowerCase();

  if (sourceType === "resident" || sourceType === "contribution") {
    return receipt?.contributionType || "Contribution";
  }

  if (sourceType === "donation" || sourceType === "other") {
    return receipt?.donationType || receipt?.otherType || "Donation";
  }

  return receipt?.otherType || receipt?.type || "Fund";
};

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

export default function DownloadReceipt() {
  const { eventId = "" } = useParams();
  const [searchParams] = useSearchParams();

  const linkedCommunityId = String(searchParams.get("communityId") || "")
    .trim()
    .toUpperCase();

  const [communityCode, setCommunityCode] = useState("");

  const [communityVerified, setCommunityVerified] = useState(false);

  const [community, setCommunity] = useState(null);

  const [currentEvent, setCurrentEvent] = useState(null);

  const [loadingPage, setLoadingPage] = useState(true);

  const [selectedBlockKey, setSelectedBlockKey] = useState("");

  const [selectedFloorNumber, setSelectedFloorNumber] = useState("");

  const [selectedFlatLabel, setSelectedFlatLabel] = useState("");

  const [receipts, setReceipts] = useState([]);

  const [findingReceipts, setFindingReceipts] = useState(false);

  const [downloadingReceiptId, setDownloadingReceiptId] = useState("");

  const [error, setError] = useState("");

  const [message, setMessage] = useState("");

  const blocks = useMemo(() => {
    if (!Array.isArray(community?.blocks)) {
      return [];
    }

    return community.blocks;
  }, [community]);

  const selectedBlock = useMemo(
    () =>
      blocks.find(
        (block, index) => getBlockKey(block, index) === selectedBlockKey,
      ) || null,
    [blocks, selectedBlockKey],
  );

  const selectedBlockName = useMemo(() => {
    if (!selectedBlock) {
      return "";
    }

    const index = blocks.findIndex((block) => block === selectedBlock);

    return normalizeBlockName(selectedBlock, index >= 0 ? index : 0);
  }, [blocks, selectedBlock]);

  const floorOptions = useMemo(
    () => buildFloorOptions(selectedBlock),
    [selectedBlock],
  );

  const flatOptions = useMemo(
    () =>
      buildFlatOptions(selectedBlock, selectedBlockName, selectedFloorNumber),
    [selectedBlock, selectedBlockName, selectedFloorNumber],
  );

  const clearMessages = () => {
    setError("");
    setMessage("");
  };

  const resetSelections = () => {
    setSelectedBlockKey("");
    setSelectedFloorNumber("");
    setSelectedFlatLabel("");
    setReceipts([]);
  };

  /* ------------------------------------------------------------------------ */
  /* Load event and community from copied link                                */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    let active = true;

    const loadPageData = async () => {
      setLoadingPage(true);
      setError("");
      setMessage("");
      resetSelections();

      if (!eventId) {
        setError("Invalid receipt link. Event ID is missing.");
        setLoadingPage(false);
        return;
      }

      if (!linkedCommunityId) {
        setError("Invalid receipt link. Community ID is missing.");
        setLoadingPage(false);
        return;
      }

      try {
        const eventSnapshot = await getDoc(doc(db, "events", eventId));

        if (!eventSnapshot.exists()) {
          throw new Error("Event not found.");
        }

        setCurrentEvent({
          id: eventSnapshot.id,
          ...eventSnapshot.data(),
        });

        if (!eventSnapshot.exists()) {
          throw new Error("Event not found.");
        }
        const eventData = {
          id: eventSnapshot.id,
          ...eventSnapshot.data(),
        };

        if (
          eventData.communityId &&
          String(eventData.communityId).trim().toUpperCase() !==
            linkedCommunityId
        ) {
          throw new Error(
            "This event does not belong to the supplied community.",
          );
        }

        if (!active) {
          return;
        }

        setMessage("Enter Your Community Code.");
      } catch (loadError) {
        console.error(
          "Load receipt page error:",
          loadError?.code,
          loadError?.message,
          loadError,
        );

        if (active) {
          setCommunity(null);
          setCurrentEvent(null);
          setError(getReadableError(loadError));
        }
      } finally {
        if (active) {
          setLoadingPage(false);
        }
      }
    };

    loadPageData();

    return () => {
      active = false;
    };
  }, [eventId, linkedCommunityId]);

  const verifyCommunity = async () => {
    try {
      setError("");
      setMessage("");
      setCommunityVerified(false);
      setCommunity(null);
      resetSelections();

      const code = String(communityCode || "")
        .trim()
        .toUpperCase();

      if (!code) {
        setError("Please enter Community ID.");
        return;
      }

      if (!currentEvent?.id) {
        setError("Event information is not loaded.");
        return;
      }

      const eventCommunityId = String(currentEvent.communityId || "")
        .trim()
        .toUpperCase();

      /*
       * The URL already contains a communityId.
       * Make sure the entered ID matches it.
       */
      if (linkedCommunityId && code !== linkedCommunityId) {
        setError("The entered Community ID does not match the receipt link.");
        return;
      }

      /*
       * Make sure the event is linked to the
       * entered community.
       */
      if (eventCommunityId && code !== eventCommunityId) {
        setError("This event does not belong to the entered community.");
        return;
      }

      const communitySnap = await getDoc(doc(db, "communities", code));

      if (!communitySnap.exists()) {
        setError("Community not found.");
        return;
      }

      const communityData = {
        id: communitySnap.id,
        ...communitySnap.data(),
      };

      if (
        !Array.isArray(communityData.blocks) ||
        communityData.blocks.length === 0
      ) {
        setError("No blocks are configured for this community.");
        return;
      }

      setCommunity(communityData);
      setCommunityVerified(true);

      setMessage("Community verified successfully. Select your flat.");
    } catch (verificationError) {
      console.error(
        "Verify community error:",
        verificationError?.code,
        verificationError?.message,
        verificationError,
      );

      setCommunityVerified(false);
      setCommunity(null);

      setError(getReadableError(verificationError));
    }
  };
  const handleBlockChange = (changeEvent) => {
    setSelectedBlockKey(changeEvent.target.value);
    setSelectedFloorNumber("");
    setSelectedFlatLabel("");
    setReceipts([]);
    clearMessages();
  };

  const handleFloorChange = (changeEvent) => {
    setSelectedFloorNumber(changeEvent.target.value);
    setSelectedFlatLabel("");
    setReceipts([]);
    clearMessages();
  };

  const handleFlatChange = (changeEvent) => {
    setSelectedFlatLabel(changeEvent.target.value);
    setReceipts([]);
    clearMessages();
  };

  /* ------------------------------------------------------------------------ */
  /* Find receipts for current event only                                     */
  /* ------------------------------------------------------------------------ */

  const handleFindReceipts = async () => {
    clearMessages();
    setReceipts([]);

    if (!eventId) {
      setError("Event ID is missing.");
      return;
    }

    if (!currentEvent?.id) {
      setError("Event information is not loaded.");
      return;
    }

    if (!community?.id) {
      setError("Community information is not loaded.");
      return;
    }
    if (!communityVerified) {
      setError("Please verify community first.");
      return;
    }

    if (!selectedBlockName) {
      setError("Please select a block.");
      return;
    }

    if (selectedFloorNumber === "") {
      setError("Please select a floor.");
      return;
    }

    if (!selectedFlatLabel) {
      setError("Please select a flat.");
      return;
    }

    try {
      setFindingReceipts(true);

      /*
       * Important:
       *
       * This queries only:
       * events/{eventId}/funds
       *
       * It does not search funds from previous events.
       */
      const receiptsQuery = query(
        collection(db, "events", eventId, "funds"),
        where("communityId", "==", community.id),
        where("blockName", "==", selectedBlockName),
        where("flatLabel", "==", selectedFlatLabel),
        limit(50),
      );

      console.log("Running current-event receipt query:", {
        eventId,
        communityId: community.id,
        blockName: selectedBlockName,
        floorNumber: selectedFloorNumber,
        flatLabel: selectedFlatLabel,
      });

      const receiptSnapshot = await getDocs(receiptsQuery);

      const selectedFloor = Number(selectedFloorNumber);

      const receiptResults = receiptSnapshot.docs
        .map((fundDocument) => {
          const fundData = fundDocument.data();

          return {
            id: fundDocument.id,
            ...fundData,
            eventId,
            eventData: currentEvent,
          };
        })

        .filter((receipt) => {
          /*
           * Extra validation.
           * The document already came from the
           * current event path.
           */
          if (receipt.eventId && String(receipt.eventId) !== String(eventId)) {
            return false;
          }

          const receiptFloor =
            receipt.floorNumber !== undefined && receipt.floorNumber !== null
              ? Number(receipt.floorNumber)
              : extractFloor(receipt.flatLabel);

          return receiptFloor === selectedFloor;
        })
        .sort(
          (firstReceipt, secondReceipt) =>
            getTimestampValue(secondReceipt.createdAt) -
            getTimestampValue(firstReceipt.createdAt),
        );

      setReceipts(receiptResults);

      if (receiptResults.length === 0) {
        setError(
          `No payment was found for ${
            currentEvent.name || currentEvent.title || "this event"
          } and the selected flat.`,
        );
        return;
      }

    } catch (searchError) {
      console.error(
        "Current-event receipt search error:",
        searchError?.code,
        searchError?.message,
        searchError,
      );

      setError(getReadableError(searchError));
    } finally {
      setFindingReceipts(false);
    }
  };

  /* ------------------------------------------------------------------------ */
  /* Download receipt                                                         */
  /* ------------------------------------------------------------------------ */

  const handleDownloadReceipt = async (receipt) => {
    setError("");

    if (!receipt?.id) {
      setError("Invalid receipt selected.");
      return;
    }

    if (!community?.id) {
      setError("Community information is missing.");
      return;
    }

    if (!currentEvent?.id) {
      setError("Event information is missing.");
      return;
    }

    setDownloadingReceiptId(receipt.id);

    try {
      await Promise.resolve(
        generateFundReceipt({
          fund: {
            ...receipt,
            eventId: currentEvent.id,
            receivedByName: receipt.receivedByName || "",
          },
          community: {
            ...community,
            id: community.id,
          },
          event: currentEvent,
          residents: [],
        }),
      );
    } catch (downloadError) {
      console.error(
        "Download receipt error:",
        downloadError?.code,
        downloadError?.message,
        downloadError,
      );

      setError(downloadError?.message || "Failed to generate the receipt.");
    } finally {
      setDownloadingReceiptId("");
    }
  };

  /* ------------------------------------------------------------------------ */
  /* Render                                                                   */
  /* ------------------------------------------------------------------------ */

  return (
    <Container className="py-4 py-md-5">
      <Row className="justify-content-center">
        <Col xs={12} md={10} lg={9} xl={8}>
          <Card className="shadow-sm rounded-4">
            <Card.Header className="py-3">
              <h4 className="mb-1">Download Receipt</h4>
              {currentEvent && (
                <div className="mt-2">
                  <Badge bg="primary">
                    Event:{" "}
                    {currentEvent.name || currentEvent.title || currentEvent.id}
                  </Badge>
                </div>
              )}
            </Card.Header>

            <Card.Body className="p-3 p-md-4">
              {error && <Alert variant="danger">{error}</Alert>}

              {message && <Alert variant="success">{message}</Alert>}

              {loadingPage ? (
                <div className="text-center py-4">
                  <Spinner animation="border" className="me-2" />
                  Loading event and community...
                </div>
              ) : (
                <>
                  <Row className="g-2 mb-3">
                    <Col md={8}>
                      <Form.Label>Community ID</Form.Label>

                      <Form.Control
                        value={communityCode}
                        onChange={(e) => {
                          setCommunityCode(e.target.value.toUpperCase());

                          setCommunityVerified(false);
                          setCommunity(null);
                          setReceipts([]);
                        }}
                        placeholder="Enter Community ID"
                      />
                    </Col>

                    <Col md={4}>
                      <Form.Label>&nbsp;</Form.Label>

                      <div className="d-grid">
                        <Button variant="primary" onClick={verifyCommunity}>
                          Verify Community
                        </Button>
                      </div>
                    </Col>
                  </Row>
                  {communityVerified && community && currentEvent && (
                    <>
                      <Alert variant="info">
                        <div>
                          <strong>Community:</strong>{" "}
                          {community.name ||
                            community.communityName ||
                            community.id}
                        </div>

                        <div>
                          <strong>Event:</strong>{" "}
                          {currentEvent.name ||
                            currentEvent.title ||
                            currentEvent.id}
                        </div>

                        {community.address && (
                          <div>
                            <strong>Address:</strong> {community.address}
                          </div>
                        )}
                      </Alert>

                      <Row className="g-3">
                        <Col xs={12} md={4}>
                          <Form.Label>Block</Form.Label>

                          <Form.Select
                            value={selectedBlockKey}
                            onChange={handleBlockChange}
                          >
                            <option value="">Select Block</option>

                            {blocks.map((block, index) => {
                              const blockKey = getBlockKey(block, index);

                              const blockName = normalizeBlockName(
                                block,
                                index,
                              );

                              return (
                                <option key={blockKey} value={blockKey}>
                                  {blockName}
                                </option>
                              );
                            })}
                          </Form.Select>
                        </Col>

                        <Col xs={12} md={4}>
                          <Form.Label>Floor</Form.Label>

                          <Form.Select
                            value={selectedFloorNumber}
                            disabled={!selectedBlockKey}
                            onChange={handleFloorChange}
                          >
                            <option value="">Select Floor</option>

                            {floorOptions.map((floorOption) => (
                              <option
                                key={floorOption.value}
                                value={floorOption.value}
                              >
                                {floorOption.label}
                              </option>
                            ))}
                          </Form.Select>
                        </Col>

                        <Col xs={12} md={4}>
                          <Form.Label>Flat</Form.Label>

                          <Form.Select
                            value={selectedFlatLabel}
                            disabled={
                              !selectedBlockKey || selectedFloorNumber === ""
                            }
                            onChange={handleFlatChange}
                          >
                            <option value="">Select Flat</option>

                            {flatOptions.map((flatLabel) => (
                              <option key={flatLabel} value={flatLabel}>
                                {flatLabel}
                              </option>
                            ))}
                          </Form.Select>
                        </Col>
                      </Row>

                      {selectedBlockKey &&
                        selectedFloorNumber !== "" &&
                        flatOptions.length === 0 && (
                          <Alert variant="warning" className="small mt-3 mb-0">
                            No flats are configured for the selected floor.
                          </Alert>
                        )}

                      <div className="d-grid mt-3">
                        <Button
                          type="button"
                          variant="success"
                          onClick={handleFindReceipts}
                          disabled={
                            findingReceipts ||
                            !selectedBlockKey ||
                            selectedFloorNumber === "" ||
                            !selectedFlatLabel
                          }
                        >
                          {findingReceipts ? (
                            <>
                              <Spinner
                                animation="border"
                                size="sm"
                                className="me-2"
                              />
                              Finding Receipt...
                            </>
                          ) : (
                            <>
                              <i className="bi bi-search me-2" />
                              Find Receipt
                            </>
                          )}
                        </Button>
                      </div>
                    </>
                  )}
                </>
              )}
            </Card.Body>
          </Card>

          {receipts.length > 0 && (
            <Card className="shadow-sm rounded-4 mt-3">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <strong>Current Event Receipts <Badge bg="primary">{receipts.length}</Badge></strong>

                
                <strong>Event Name :  {" "} {currentEvent.name ||
                              currentEvent.title ||
                              "Community Event"} </strong>
              </Card.Header>


              <Card.Body className="p-0">
                <Table
                  responsive
                  bordered
                  hover
                  size="sm"
                  className="mb-0 align-middle"
                >
                  <thead>
                    <tr>
                     
                      {/* <th>Event</th> */}
                       
                      <th>Name</th>
                      <th>Type</th>
                     
                      <th>Amount</th>
                       <th>Rec No</th>
                      <th>Mode</th>
                      <th>Receiver</th>
                      {/* <th>Date</th> */}
                      <th>Download</th>
                    </tr>
                  </thead>

                  <tbody>
                    {receipts.map((receipt) => {
                      const isDownloading = downloadingReceiptId === receipt.id;

                      return (
                        <tr key={receipt.id}>
                          
                          {/* <td>
                            {currentEvent.name ||
                              currentEvent.title ||
                              "Community Event"}
                          </td> */}
                           <td>
                            {receipt.paidByName ||
                              receipt.name
                             }
                          </td>
                          <td>
                            {receipt.sourceType === "resident"
                              ? "Contribution"
                              : receipt.otherType || "Other"}
                          </td>

                         
                         

                          <td className="text-success fw-bold text-nowrap" style={{ minWidth: "140px" }}>
  ₹ {formatAmount(receipt.amount)}
  
</td>
 <td>
                            {receipt.receiptNo ||
                              receipt.receiptNumber ||
                              receipt.id}
                          </td>
                          <td>
                            {receipt.method || receipt.paymentMode || "-"}
                          </td>

                          <td>{receipt.receivedByName || "-"}</td>

                          {/* <td>{formatDate(receipt.createdAt)}</td> */}

                          <td>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline-primary"
                              disabled={isDownloading}
                              onClick={() => handleDownloadReceipt(receipt)}
                            >
                              {isDownloading ? (
                                <>
                                  <Spinner
                                    animation="border"
                                    size="sm"
                                    className="me-2"
                                  />
                                  Downloading...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-file-earmark-pdf me-1" />
                                  Download
                                </>
                              )}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}

          <Alert variant="warning" className="small mt-3 mb-5">
            This link limits the search to one event, but anyone with the link
            and unit information may still search that event's receipts. For
            stronger protection, require resident authentication or registered
            mobile-number verification.
          </Alert>
        </Col>
      </Row>
    </Container>
  );
}
